<!-- src/views/client/DeviceDetails.vue -->
<template>
  <client-layout :title="device ? device.name : $t('ficheAppareil.chargementTitre')">

    <!-- Bannière perte WebSocket -->
    <div v-if="!wsConnected" class="mb-4 flex items-center justify-between gap-3 px-4 py-3 rounded-lg bg-yellow-50 border border-yellow-300 text-yellow-800 text-sm font-medium">
      <div class="flex items-center gap-2">
        <span class="inline-block w-2 h-2 rounded-full bg-yellow-500 animate-pulse flex-shrink-0"></span>
        {{ $t('ficheAppareil.wsPerdue') }}
      </div>
      <button @click="reloadDevice" class="flex-shrink-0 px-3 py-1 rounded border border-yellow-400 bg-yellow-100 hover:bg-yellow-200 text-yellow-800 text-xs font-semibold transition-colors">
        {{ $t('ficheAppareil.actualiser') }}
      </button>
    </div>

    <!-- Alarme non vue (B11) : en tête de fiche, au-dessus de tout le reste. -->
    <alarme-non-vue v-if="device" class="mb-4" :device="device" @vue="device.alarm_unseen = null" />

    <!--
      Perte de protection. Le capteur filaire a été déclaré en panne par la carte, qui a SUSPENDU
      la surveillance de l'entrée : pendant ce temps, un déclenchement réel ne remontera pas.
      Ce n'est pas un avertissement technique à ranger dans l'historique — c'est une zone qui a
      cessé d'être protégée, et ça reste affiché tant que ça dure.
    -->
    <div v-if="wiredFault" class="mb-4 flex items-start gap-3 px-4 py-3 rounded-lg bg-red-50 border border-red-300 text-red-800 text-sm">
      <span class="inline-block w-2 h-2 mt-1.5 rounded-full bg-red-500 animate-pulse flex-shrink-0"></span>
      <div>
        <div class="font-semibold">{{ $t('ficheAppareil.panneTitre') }}</div>
        <div class="mt-0.5">
          {{ $t('ficheAppareil.panneTexte') }}
          <span v-if="device && device.wired_fault_since"> {{ $t('ficheAppareil.panneDepuis', { date: formatDate(device.wired_fault_since) }) }}</span>
        </div>
      </div>
    </div>

    <!--
      Armé alors que l'entrée filaire était déjà ouverte. Le client croit son installation armée —
      elle l'est, mais cette zone ne protège pas. C'est précisément le trou silencieux que le
      firmware a choisi d'éviter en armant et en prévenant plutôt qu'en refusant.
    -->
    <div v-if="armZoneOpen" class="mb-4 flex items-start gap-3 px-4 py-3 rounded-lg bg-amber-50 border border-amber-300 text-amber-900 text-sm">
      <span class="inline-block w-2 h-2 mt-1.5 rounded-full bg-amber-500 flex-shrink-0"></span>
      <div>
        <div class="font-semibold">{{ $t('ficheAppareil.armZoneTitre') }}</div>
        <i18n-t keypath="ficheAppareil.armZoneTexte" tag="div" class="mt-0.5">
          <template #origine>{{ armZoneOpenOrigine }}</template>
          <template #quand><span v-if="device && device.arm_zone_open_at">{{ $t('ficheAppareil.armZoneQuand', { date: formatDate(device.arm_zone_open_at) }) }}</span></template>
        </i18n-t>
      </div>
    </div>

    <div v-if="loading" class="flex justify-center items-center h-64">
      <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
    </div>

    <div v-else-if="!device" class="text-center py-12 text-gray-500">{{ $t('ficheAppareil.introuvable') }}</div>

    <div v-else>
      <!-- En-tête état live -->
      <div class="bg-white rounded-lg shadow p-4 mb-6">
        <div class="flex flex-wrap justify-between items-center gap-3">
          <div>
            <h2 class="text-xl font-bold text-gray-900">{{ device.name }}</h2>
            <p class="text-sm text-gray-500">{{ device.location }}</p>
          </div>
          <div class="flex flex-wrap gap-2">
            <span :class="`px-3 py-1 rounded-full text-sm font-semibold ${stateBadge}`">{{ stateLabel }}</span>
            <span :class="`px-3 py-1 rounded-full text-sm font-semibold ${powerBadge}`">{{ powerLabel }}</span>
            <span v-if="device.bat_pct !== null && device.bat_pct !== undefined"
                  class="px-3 py-1 rounded-full text-sm font-semibold bg-gray-100 text-gray-700">
              🔋 {{ device.bat_pct }}%<span v-if="device.cap" class="font-normal text-gray-500"> · {{ device.cap }} mA·h</span>
            </span>
            <span v-if="device.gsm_rssi !== null && device.gsm_rssi !== undefined && device.gsm_rssi !== 99"
                  class="px-3 py-1 rounded-full text-sm font-semibold bg-gray-100 text-gray-700">
              📶 {{ device.gsm_rssi }}/31
            </span>
            <span v-if="device.temp !== null && device.temp !== undefined"
                  class="px-3 py-1 rounded-full text-sm font-semibold bg-gray-100 text-gray-700">
              🌡 {{ device.temp }}°C
            </span>
            <!-- Mode éco (réservé au mode solaire — batterie faible avec panneau solaire) -->
            <span v-if="device.eco_mode && device.solar_mode"
                  class="px-3 py-1 rounded-full text-sm font-semibold bg-red-100 text-red-700 animate-pulse">
              🔋 {{ $t('etat.ecoMode') }}
            </span>
            <!-- Fenêtre de maintenance -->
            <span v-if="maintWindowOpen"
                  class="px-3 py-1 rounded-full text-sm font-semibold bg-purple-100 text-purple-700 animate-pulse">
              🔧 {{ $t('ficheAppareil.maintenance', { s: maintCountdown }) }}
            </span>
          </div>
        </div>
        <div v-if="device.last_seen" class="mt-2 text-xs text-gray-400">
          {{ $t('ficheAppareil.dernierMessage', { date: formatDate(device.last_seen) }) }}
        </div>
      </div>

      <!-- Onglets -->
      <div class="border-b border-gray-200 mb-6">
        <nav class="-mb-px flex space-x-6 overflow-x-auto">
          <button v-for="tab in tabs" :key="tab.id"
                  @click="activeTab = tab.id"
                  :class="`pb-3 px-1 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`">
            {{ tab.label }}
          </button>
        </nav>
      </div>

      <!-- Onglet : Accessoires -->
      <!-- Onglet : Accessoires. Composant partagé avec l'espace admin. Sans renommage, le
           client vit avec « Accessoire 14532 » et son historique d'alarmes devient illisible
           au moment où il compte. Les identifiants radio (`technique`) lui sont masqués : il
           n'a aucun moyen de s'en servir. Le retrait global reste dans l'onglet Commandes,
           où il se trouvait déjà. -->
      <div v-if="activeTab === 'accessories'">
        <accessories-panel :device-id="deviceId" :accessories="accessories" :modifiable="estTitulaire"
                           @maj="accessories = $event" />
      </div>

      <!-- Onglet : Contacts. Le composant est partagé avec l'espace admin : le backend
           autorisait déjà le client (routes en `hasDeviceAccess`), seul l'écran n'avait
           jamais eu de boutons. -->
      <div v-if="activeTab === 'contacts'">
        <contacts-panel :device-id="deviceId" :contacts="contacts" :modifiable="estTitulaire" @maj="contacts = $event" />
      </div>

      <!-- Onglet : Planning. Modifiable par le client — ce sont SES horaires d'activité, et
           lui seul les connaît. Le bouton « Envoyer » lui est ouvert depuis le 28/09. -->
      <div v-if="activeTab === 'schedule'">
        <device-schedule :device-id="deviceId" :modifiable="estTitulaire" />
      </div>

      <!-- Onglet : Batterie -->
      <div v-if="activeTab === 'battery'">
        <battery-panel-client :device-id="deviceId" />
      </div>

      <!-- Onglet : État — ce que la CARTE rapporte, pas ce qu'on lui a demandé. La nuance
           compte : un réglage envoyé à un dormeur n'est appliqué qu'à son réveil. -->
      <div v-if="activeTab === 'status'">
        <div class="bg-white rounded-lg shadow overflow-hidden">
          <div class="px-4 py-3 border-b border-gray-200">
            <h3 class="font-semibold text-gray-800">{{ $t('ficheAppareil.etatTitre') }}</h3>
          </div>
          <dl class="divide-y divide-gray-100">
            <div v-for="l in lignesEtat" :key="l.label" class="px-4 py-3 flex justify-between gap-4 text-sm">
              <dt class="text-gray-500">{{ l.label }}</dt>
              <dd class="text-gray-900 text-right">{{ l.valeur }}</dd>
            </div>
          </dl>
        </div>
        <!-- L'IMEI ne sert qu'au support : à part, replié, jamais en tête de l'état (cahier §3). -->
        <details class="mt-3 bg-white rounded-lg shadow px-4 py-3 text-sm">
          <summary class="cursor-pointer text-gray-600">{{ $t('ficheAppareil.aPropos') }}</summary>
          <dl class="mt-2 flex justify-between gap-4">
            <dt class="text-gray-500">{{ $t('ficheAppareil.ligneImei') }}</dt>
            <dd class="text-gray-900 font-mono">{{ device && device.ident || '—' }}</dd>
          </dl>
        </details>
      </div>

      <!-- Onglet : Commandes -->
      <!--
        Les boutons sont rendus d'après `allowed_commands`, que le SERVEUR renvoie avec la
        fiche. C'est volontaire : jusqu'au 16/09 la distinction client/admin n'existait QUE
        dans l'interface — la route acceptait n'importe quelle commande d'un propriétaire, et
        cacher un bouton ne protégeait rien. Le filtre vit maintenant dans le contrôleur
        (constants/client-commands.js) et l'interface ne fait que refléter ce qu'il autorise :
        une commande ajoutée là-bas apparaît ici, et aucune liste ne dérive de l'autre.
      -->
      <div v-if="activeTab === 'commands'" class="space-y-4">
        <div v-if="cmdMessage" :class="cmdOk ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-700'"
             class="rounded-md border p-3 text-sm">{{ cmdMessage }}</div>

        <div v-if="peut('arm') || peut('disarm')" class="bg-white rounded-lg shadow p-4">
          <h3 class="font-semibold text-gray-800">{{ $t('ficheAppareil.armementTitre') }}</h3>
          <i18n-t keypath="ficheAppareil.etatActuel" tag="p" class="mt-1 text-sm text-gray-500"><template #etat><strong>{{ stateLabel }}</strong></template></i18n-t>
          <div class="mt-3 flex flex-wrap gap-2">
            <button v-if="peut('arm')" :disabled="cmdBusy || injoignable" @click="envoyer('arm')"
                    class="px-4 py-2 rounded-md bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed">{{ $t('ficheAppareil.armer') }}</button>
            <button v-if="peut('disarm')" :disabled="cmdBusy || injoignable" @click="envoyer('disarm')"
                    class="px-4 py-2 rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed">{{ $t('ficheAppareil.desarmer') }}</button>
          </div>
          <!-- Carte endormie hors fenêtre de maintenance : boutons grisés. L'ancien texte disait
               « la commande peut n'être appliquée qu'au prochain rapport » : FAUX, elle était
               perdue (ni retenue ni rejouée, aucune fenêtre d'écoute après un réveil). -->
          <p v-if="injoignable" class="mt-2 text-xs text-amber-700">{{ messageEndormie() }}</p>
        </div>

        <div v-if="peut('status') || peut('close_window')" class="bg-white rounded-lg shadow p-4">
          <h3 class="font-semibold text-gray-800">{{ $t('ficheAppareil.etatCarteTitre') }}</h3>
          <div class="mt-3 flex flex-wrap gap-2">
            <button v-if="peut('status')" :disabled="cmdBusy || injoignable" @click="envoyer('status')"
                    class="btn btn-secondary text-sm disabled:opacity-50 disabled:cursor-not-allowed">{{ $t('ficheAppareil.demanderRapport') }}</button>
            <button v-if="peut('close_window') && maintWindowOpen" :disabled="cmdBusy" @click="envoyer('close_window')"
                    class="px-4 py-2 rounded-md border border-purple-300 text-purple-700 hover:bg-purple-50">
              {{ $t('ficheAppareil.fermerFenetre') }}
            </button>
          </div>
        </div>

        <!-- Jumelage, choc, GPS, entrée filaire, cadences : composants PARTAGÉS avec l'espace
             admin (components/device/commands/), pour que les deux vues ne dérivent plus. -->
        <pairing-card v-if="peut('pair_start')" :device-id="deviceId" :send="envoyer" :busy="cmdBusy" :injoignable="injoignable">
          <template #actions>
            <button v-if="peut('unpair_all') && accessories.length" :disabled="cmdBusy"
                    @click="confirmerPuisEnvoyer('unpair_all', $t('ficheAppareil.confirmerRetirerTous', { n: accessories.length }))"
                    class="btn text-sm border border-red-300 text-red-700 hover:bg-red-50">
              {{ $t('ficheAppareil.retirerTous') }}
            </button>
          </template>
        </pairing-card>

        <!--
          Deux encadrés et non un seul « Détection ». La sensibilité au choc décide de ce qui
          DÉCLENCHE une alarme, le GPS dit seulement OÙ se trouve la carte et ne déclenche rien.
          Les ranger ensemble laissait croire qu'éteindre le GPS affaiblissait la détection.
        -->
        <accel-card v-if="peut('accel_config')" :device="device" :send="envoyer" :busy="cmdBusy" />
        <gnss-card v-if="peut('gnss_enable')" :device="device" :send="envoyer" :busy="cmdBusy" />
        <wired-input-card v-if="peut('alarm_24h_enable') || peut('alarm_siren_enable')"
                          :device="device" :send="envoyer" :busy="cmdBusy" :peut="peut" />
        <report-intervals-cards v-if="peut('set_daily_interval')" :device="device" :send="envoyer" :busy="cmdBusy" />

        <div v-if="peut('reboot')" class="bg-white rounded-lg shadow p-4">
          <h3 class="font-semibold text-gray-800">{{ $t('ficheAppareil.maintenanceTitre') }}</h3>
          <button :disabled="cmdBusy"
                  @click="confirmerPuisEnvoyer('reboot', $t('ficheAppareil.confirmerReboot'))"
                  class="btn btn-warning text-sm mt-3">
            {{ $t('ficheAppareil.redemarrer') }}
          </button>
          <!-- Fait de RUNBOOK §2, dit ici pour éviter un appel au support : sur batterie hors
               fenêtre PANIC, le firmware refuse le redémarrage SANS RIEN DIRE. -->
          <p class="mt-2 text-xs text-gray-500">
            {{ $t('ficheAppareil.rebootNote') }}
          </p>
        </div>
      </div>

      <!-- Onglet : GPS -->
      <div v-if="activeTab === 'gps'">
        <!-- Même carte que l'admin. L'ancien encadré lisait `device.latitude` /
             `device.longitude`, colonnes qui N'EXISTENT PAS : la position vit dans `last_lat` /
             `last_lon`. Le client lisait donc « aucune position reçue » sur une carte dont
             l'admin voyait la dernière position. -->
        <device-map :position="lastGnssPosition" />
      </div>

      <!-- Onglet : Événements -->
      <div v-if="activeTab === 'events'">
        <event-list :events="events" :title="$t('ficheAppareil.historique')"
                    :more-link="{ path: '/client/events', query: { deviceId: $route.params.deviceId } }" />
      </div>
    </div>
  </client-layout>
</template>

<script>
import { defineComponent, ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { format, parseISO } from 'date-fns';
import { useI18n } from 'vue-i18n';
import { localeDateFns, enParis } from '@/i18n/dates';
import { texteErreur, texteSucces } from '@/i18n/serveur';
import ClientLayout   from '@/components/layout/ClientLayout.vue';
import EventList      from '@/components/common/EventList.vue';
import DeviceSchedule from '@/components/device/DeviceSchedule.vue';
import ContactsPanel from '@/components/device/ContactsPanel.vue';
import AccessoriesPanel from '@/components/device/AccessoriesPanel.vue';
import BatteryPanelClient from '@/components/common/BatteryPanelClient.vue';
import { versionLabel } from '@/utils/fwVersion';
import DeviceMap from '@/components/device/DeviceMap.vue';
import AlarmeNonVue from '@/components/device/AlarmeNonVue.vue';
import PairingCard from '@/components/device/commands/PairingCard.vue';
import AccelCard from '@/components/device/commands/AccelCard.vue';
import GnssCard from '@/components/device/commands/GnssCard.vue';
import WiredInputCard from '@/components/device/commands/WiredInputCard.vue';
import ReportIntervalsCards from '@/components/device/commands/ReportIntervalsCards.vue';
import apiService from '@/services/api.service';
import socketService from '@/services/socket.service';
import { carteEndormie, messageEndormie } from '@/utils/carte';

export default defineComponent({
  name: 'ClientDeviceDetails',
  components: { ClientLayout, EventList, AlarmeNonVue, DeviceSchedule, ContactsPanel, AccessoriesPanel, BatteryPanelClient,
                DeviceMap, PairingCard, AccelCard, GnssCard, WiredInputCard, ReportIntervalsCards },

  setup() {
    const store    = useStore();
    const { t }    = useI18n();
    const route    = useRoute();
    const deviceId = parseInt(route.params.deviceId);

    const loading     = ref(true);
    const device      = ref(null);
    const accessories = ref([]);
    const contacts    = ref([]);
    const events      = ref([]);
    const lastGnssPosition = ref(null);

    const activeTab = ref('status');

    // Comptes partagés : un membre consulte contacts, accessoires et planning ; seul le
    // titulaire les modifie. Le serveur refuse de toute façon ; l'écran ne propose pas un geste
    // qui échouerait. Les commandes, elles, suivent `allowed_commands` (calculé par le serveur).
    const estTitulaire = computed(() => { const u = store.getters['auth/user']; return !u || !u.memberRole || u.memberRole === 'titulaire'; });

    // --- Commandes ------------------------------------------------------------------------
    // `allowed` vient du serveur : `null` = aucune restriction (admin), sinon la liste exacte.
    const allowed     = ref([]);
    const cmdBusy     = ref(false);
    const cmdMessage  = ref(null);
    const cmdOk       = ref(false);

    // Les onglets du client couvrent désormais ce que l'admin voit, moins ce qui relève du
    // dépannage. « Commandes » n'apparaît que si le serveur autorise au moins une action.
    const tabs = computed(() => [
      { id: 'status',      label: t('ficheAppareil.ongletEtat') },
      { id: 'commands',    label: t('ficheAppareil.ongletCommandes') },
      { id: 'accessories', label: t('ficheAppareil.ongletAccessoires') },
      { id: 'contacts',    label: t('ficheAppareil.ongletContacts') },
      { id: 'schedule',    label: t('ficheAppareil.ongletPlanning') },
      { id: 'battery',     label: t('ficheAppareil.ongletBatterie') },
      { id: 'gps',         label: t('ficheAppareil.ongletGps') },
      { id: 'events',      label: t('ficheAppareil.ongletEvenements') }
    ].filter(t => t.id !== 'commands' || (allowed.value === null || allowed.value.length)));

    const peut = a => allowed.value === null || allowed.value.includes(a);

    // Message de succès : le serveur renvoie un `code` (règle de texteSucces, i18n/serveur.js).
    const messageSucces = (command, r) => texteSucces(r, t('ficheAppareil.commandeTransmise'));

    // Renvoie `true` si le serveur a accepté : le jumelage ne lance son décompte qu'à cette
    // condition, et affiche lui-même son résultat.
    const envoyer = async (command, data) => {
      cmdBusy.value = true; cmdMessage.value = null;
      try {
        const r = await store.dispatch('device/sendCommand', { deviceId, command, data });
        if (command !== 'pair_start') {
          cmdOk.value = true;
          cmdMessage.value = messageSucces(command, r);
        }
        return true;
      } catch (e) {
        cmdOk.value = false;
        // Le 403 du serveur porte un message explicite : on l'affiche tel quel plutôt que de
        // le traduire en « erreur », qui n'aiderait personne.
        cmdMessage.value = texteErreur(e, t('ficheAppareil.commandeEchec'));
        return false;
      } finally {
        cmdBusy.value = false;
      }
    };

    // Deux actions sont irréversibles d'un clic : retirer tous les accessoires et redémarrer.
    const confirmerPuisEnvoyer = (command, question) => {
      if (window.confirm(question)) envoyer(command);
    };

    const gnssActif = computed(() => {
      const d = device.value;
      if (!d) return false;
      // L'état RAPPORTÉ fait foi quand il existe : c'est ce que la carte applique réellement.
      const rapporte = d.reported_config && d.reported_config.gnss;
      return rapporte !== undefined && rapporte !== null ? !!rapporte : !!d.gnss_enabled;
    });

    // Rôle de l'entrée filaire : calculé par le serveur (`wired_role`) et seulement affiché ici.
    // Le repli ne sert qu'à une fiche chargée avant la migration des colonnes.
    const roleFilaire = computed(() => (device.value && device.value.wired_role)
      || { h24: false, siren: true, nature: 'intrusion', report: false, warn_no_report: false });

    const wiredRoleLabel = computed(() => {
      switch (roleFilaire.value.nature) {
        case 'technique_sonore':      return t('ficheAppareil.roleSonore');
        case 'technique_silencieuse': return t('ficheAppareil.roleSilencieuse');
        default:                      return t('ficheAppareil.roleIntrusion');
      }
    });

    const wiredFault = computed(() => !!(device.value && device.value.wired_fault));

    const armZoneOpen = computed(() => !!(device.value && device.value.arm_zone_open_at));
    const armZoneOpenOrigine = computed(() => {
      const src = device.value && device.value.arm_zone_open_src;
      return src === 'schedule' ? t('ficheAppareil.origineSchedule')
           : src === 'rf'       ? t('ficheAppareil.origineRf')
           : src === 'mqtt'     ? t('ficheAppareil.origineMqtt')
           : t('ficheAppareil.origineInconnue');
    });

    // 🔴 Le niveau de l'entrée n'a qu'une source : l'événement `alarm_input`, conditionné par la
    // remontée d'état. Coupée, la valeur se fige sur la dernière transition connue — et après un
    // redémarrage de la carte, on ne sait plus rien du tout. Annoncer « au repos » au client dans
    // ces cas-là serait un faux rassurant sur un produit de sécurité : on dit « inconnu ».
    const etatEntreeFiable = computed(() => {
      const d = device.value;
      if (!d) return false;
      if (d.alarm_input_state === null || d.alarm_input_state === undefined) return false;
      return roleFilaire.value.report || d.state === 'armed';
    });

    const etatEntreeLabel = computed(() => {
      if (!etatEntreeFiable.value) return t('ficheAppareil.entreeInconnu');
      return device.value.alarm_input_state ? t('ficheAppareil.entreeActivee') : t('ficheAppareil.entreeRepos');
    });

    // Onglet État : ce que la carte RAPPORTE (`reported_config`). Une valeur jamais rapportée
    // est « inconnu » — jamais « non » ni « normal », qui affirmeraient ce qu'on ne sait pas
    // (relevé de l'agent de l'app mobile, 02/10/2026 : ces lignes lisaient la valeur DEMANDÉE).
    const CLES_CHOC = { very_high: 'veryHigh', high: 'high', medium: 'medium', low: 'low', off: 'off' };
    const lignesEtat = computed(() => {
      const d = device.value;
      if (!d) return [];
      const r = d.reported_config || {};
      const inconnu = t('ficheAppareil.entreeInconnu');
      const rapporte = v => v !== undefined && v !== null;
      const oui = v => !rapporte(v) ? inconnu : (v ? t('ficheAppareil.oui') : t('ficheAppareil.non'));
      const l = [
        { label: t('ficheAppareil.ligneFirmware'), valeur: d.fw_ver != null ? versionLabel(d.fw_ver) : inconnu },
        { label: t('ficheAppareil.ligneAlimentation'), valeur: powerLabel.value },
        { label: t('ficheAppareil.ligneArmement'), valeur: stateLabel.value },
        { label: t('ficheAppareil.ligneGps'), valeur: oui(r.gnss) },
        { label: t('ficheAppareil.ligneRadio'), valeur: oui(r.rf) },
        { label: t('ficheAppareil.ligneChoc'), valeur: !rapporte(r.accel_sens) ? inconnu
            : (CLES_CHOC[r.accel_sens] ? t(`choc.${CLES_CHOC[r.accel_sens]}`) : String(r.accel_sens)) },
        // Nom donné par le titulaire (05/10/2026) : ligne absente tant qu'il n'y en a pas.
        ...(d.wired_label ? [{ label: t('ficheAppareil.ligneNomEntree'), valeur: d.wired_label }] : []),
        { label: t('ficheAppareil.ligneEntree'), valeur: !rapporte(r.alarm_nc) ? inconnu
            : (r.alarm_nc ? t('ficheAppareil.entreeNc') : t('ficheAppareil.entreeNo')) },
        { label: t('ficheAppareil.ligneRole'), valeur: wiredRoleLabel.value },
        { label: t('ficheAppareil.ligneSirene'), valeur: !rapporte(r.alarm_siren) ? inconnu
            : (r.alarm_siren ? t('ficheAppareil.oui') : t('ficheAppareil.sireneNon')) },
        { label: t('ficheAppareil.ligneRemontee'), valeur: oui(r.alarm_report) },
        { label: t('ficheAppareil.ligneEtatEntree'),
          valeur: etatEntreeFiable.value
            ? etatEntreeLabel.value
            : (d.alarm_input_state === null || d.alarm_input_state === undefined
                ? t('ficheAppareil.inconnuDemarrage')
                : t('ficheAppareil.inconnuCoupee')) },
        { label: t('ficheAppareil.ligneCapteur'), valeur: d.wired_fault ? t('ficheAppareil.capteurPanne') : t('ficheAppareil.capteurNormal') },
        { label: t('ficheAppareil.ligneDernierMessage'), valeur: d.last_seen ? formatDate(d.last_seen) : '—' }
      ];
      return l;
    });


    const stateBadge = computed(() => {
      if (!device.value) return 'bg-gray-100 text-gray-500';
      switch (device.value.state) {
        case 'armed':     return 'bg-orange-100 text-orange-700';
        case 'triggered': return 'bg-red-100 text-red-700';
        case 'disarmed':  return 'bg-green-100 text-green-700';
        default:          return 'bg-gray-100 text-gray-500';
      }
    });
    const stateLabel = computed(() => {
      if (!device.value) return '';
      switch (device.value.state) {
        case 'armed':     return t('etat.armed');
        case 'triggered': return t('etat.triggered');
        case 'disarmed':  return t('etat.disarmed');
        // Aucun message reçu depuis le rattachement : « Inconnu » laissait croire à une panne,
        // alors que la carte n'a simplement pas encore été allumée (ou pas encore parlé).
        default:          return device.value.last_seen ? t('etat.unknown') : t('etat.jamaisConnectee');
      }
    });
    const powerBadge = computed(() => {
      if (!device.value) return 'bg-gray-100 text-gray-500';
      if (device.value.solar_mode) return 'bg-amber-100 text-amber-700';
      switch (device.value.power_source) {
        case 'mains':    return 'bg-blue-100 text-blue-700';
        case 'battery':  return 'bg-yellow-100 text-yellow-700';
        case 'solar':    return 'bg-amber-100 text-amber-700';
        default:         return 'bg-gray-100 text-gray-500';
      }
    });
    const powerLabel = computed(() => {
      if (!device.value) return '';
      if (device.value.solar_mode) return t('etat.solar');
      switch (device.value.power_source) {
        case 'mains':    return t('etat.mains');
        case 'battery':  return t('etat.battery');
        case 'solar':    return t('etat.solar');
        default:         return t('ficheAppareil.alimInconnue');
      }
    });

    const getDevType = (type) => {
      const types = { 1: t('ficheAppareil.devTelecommande'), 18: t('ficheAppareil.devPir'), 19: t('ficheAppareil.devOuverture'), 47: t('ficheAppareil.devChoc'), 49: t('ficheAppareil.devPanneau') };
      return types[type] || t('ficheAppareil.devType', { type });
    };

    const formatDate = (d) => {
      if (!d) return '';
      try { return format(enParis(parseISO(d)), "dd MMM yyyy HH:mm", { locale: localeDateFns() }); }
      catch { return d; }
    };

    // ---- Fenêtre de maintenance (batterie) ----
    const maintWindowOpen = ref(false);
    // Carte endormie hors fenêtre de maintenance : les actions immédiates sont impossibles
    // (le serveur les refuse aussi, code CARTE_ENDORMIE).
    const injoignable = computed(() => carteEndormie(device.value) && !maintWindowOpen.value);
    const maintCountdown = ref(0);
    let maintTimer = null;

    const startMaintCountdown = (seconds) => {
      maintWindowOpen.value = true;
      maintCountdown.value = seconds;
      if (maintTimer) clearInterval(maintTimer);
      maintTimer = setInterval(() => {
        maintCountdown.value--;
        if (maintCountdown.value <= 0) {
          maintWindowOpen.value = false;
          clearInterval(maintTimer);
          maintTimer = null;
        }
      }, 1000);
    };

    const stopMaintCountdown = () => {
      maintWindowOpen.value = false;
      maintCountdown.value = 0;
      if (maintTimer) { clearInterval(maintTimer); maintTimer = null; }
    };

    // Reconcile : relit `device.window_open_until` (persisté en BDD côté serveur)
    // et active/désactive le badge selon le temps restant.
    const reconcileMaintWindow = () => {
      const d = device.value;
      if (!d || !d.window_open_until) {
        stopMaintCountdown();
        return;
      }
      const remainingMs = new Date(d.window_open_until).getTime() - Date.now();
      if (remainingMs <= 0) {
        stopMaintCountdown();
      } else {
        startMaintCountdown(Math.ceil(remainingMs / 1000));
      }
    };

    const reloadDevice = async () => {
      try {
        const result  = await store.dispatch('device/fetchDetails', deviceId);
        device.value      = result.device;
        // Une seule source de vérité : ce que le serveur autorise, et rien de recopié ici.
        // `undefined` (ancienne réponse, cache) ⇒ on n'affiche aucune commande plutôt que
        // d'en proposer qui échoueraient.
        allowed.value     = result.allowed_commands === undefined ? [] : result.allowed_commands;
        accessories.value = result.accessories || [];
        contacts.value    = result.contacts || [];
        events.value      = result.events || [];
        const d = result.device;
        lastGnssPosition.value = d && d.last_lat && d.last_lon
          ? { lat: d.last_lat, lon: d.last_lon, fix: d.last_gnss_fix, sat: d.last_gnss_sat,
              src: d.last_gnss_src, timestamp: d.last_gnss_at }
          : null;
        reconcileMaintWindow();
      } catch (e) {
        console.error('Erreur rechargement device:', e);
      }
    };

    onMounted(async () => {
      try {
        await reloadDevice();
      } finally {
        loading.value = false;
      }
    });

    // ---- Socket.io temps réel ----
    const socket = socketService.connect();

    socket.on('device:update', (data) => {
      if (data.deviceId !== deviceId || !device.value) return;
      Object.assign(device.value, data.fields);
      device.value.last_seen = new Date().toISOString();
      if (Object.prototype.hasOwnProperty.call(data.fields || {}, 'window_open_until')) {
        reconcileMaintWindow();
      }
    });

    socket.on('device:event', (data) => {
      if (data.deviceId !== deviceId) return;
      events.value.unshift({
        event_type: data.eventType,
        event_message: data.message,
        // Code et paramètres neutres (s'il y en a) : l'historique les traduit (texteEvenement).
        message_code: data.code,
        message_params: data.params,
        timestamp: new Date().toISOString()
      });
      if (events.value.length > 20) events.value.pop();
    });

    socket.on('device:window', (data) => {
      if (data.deviceId !== deviceId) return;
      if (data.open) {
        startMaintCountdown(data.duration_s || 120);
      } else {
        stopMaintCountdown();
      }
    });

    socket.on('device:gnss', (data) => {
      if (data.deviceId !== deviceId) return;
      lastGnssPosition.value = { lat: data.lat, lon: data.lon, fix: data.fix, sat: data.sat,
                                 src: data.src, timestamp: data.timestamp };
    });

    // Le jumelage réussi se lit ici aussi : la liste de l'onglet Accessoires suit.
    const onAccessories = async (data) => {
      if (data.deviceId !== deviceId) return;
      try { accessories.value = (await apiService.getDeviceAccessories(deviceId)).data; }
      catch (e) { console.error('Erreur rechargement accessoires:', e); }
    };
    socket.on('device:accessories', onAccessories);

    onBeforeUnmount(() => {
      socket.off('device:gnss');
      socket.off('device:accessories', onAccessories);
      socket.off('device:update');
      socket.off('device:event');
      socket.off('device:window');
      if (maintTimer) clearInterval(maintTimer);
    });

    return {
      loading, device, accessories, contacts, events,
      deviceId, estTitulaire,
      activeTab, tabs,
      allowed, peut, envoyer, confirmerPuisEnvoyer, cmdBusy, cmdMessage, cmdOk,
      gnssActif, lignesEtat, lastGnssPosition,
      roleFilaire, wiredRoleLabel, wiredFault, etatEntreeFiable, etatEntreeLabel,
      armZoneOpen, armZoneOpenOrigine,
      stateBadge, stateLabel, powerBadge, powerLabel,
      maintWindowOpen, maintCountdown, injoignable, messageEndormie,
      getDevType, formatDate, reloadDevice,
      wsConnected: socketService.connected
    };
  }
});
</script>
